import {
  ArrowLeft,
  Ban,
  ChevronRight,
  Download,
  ExternalLink,
  FileText,
  GitBranch,
  LoaderCircle,
  RotateCcw,
  ScanLine,
  Search,
  ShieldAlert,
  ShieldCheck,
  UploadCloud,
  X,
} from "lucide-react";
import {
  type DocumentEditOutput,
  type DocumentEditorGate,
  type DocumentGitStatus,
  type DocumentProjection,
  type DocumentRedactionDerivative,
  type DocumentVersionProjection,
} from "@mind/contracts/browser";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type FormEvent, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { ApiError } from "../../api/client.js";
import { useAppSession } from "../../app/AppSession.js";
import { documentApi, createMarkdownSaveCommand, decodeBase64Utf8 } from "./documentApi.js";
import { useDocumentUploadQueue, type DocumentUploadQueueEntry } from "./useDocumentUploadQueue.js";
import { documentsQueryKeys } from "./queryKeys.js";
import { validateDocumentsSearch, withDocument, type DocumentsSearch } from "./searchState.js";
import styles from "./DocumentsScreen.module.css";
import { OfficeEditor } from "./OfficeEditor.js";

type LibraryFilter = "all" | "ready" | "scanning" | "attention";
type UploadState = DocumentProjection["ingestState"];

const FILTERS: readonly LibraryFilter[] = ["all", "ready", "scanning", "attention"];
const OFFICE_EDITOR_OPEN_TIMEOUT_MS = 15_000;
const OFFICE_EDITOR_RESET_TIMEOUT_MS = 15_000;
const OFFICE_EDITOR_FINALIZE_TIMEOUT_MS = 35_000;

export function DocumentsScreen() {
  const { t } = useTranslation();
  const session = useAppSession();
  const navigate = useNavigate();
  const routeSearch = useRouterState({ select: (state) => state.location.search });
  const search = validateDocumentsSearch(routeSearch);
  const workspace = session.workspaces.find((candidate) => candidate.id === search.workspace) ?? session.selectedWorkspace;
  const workspaceId = workspace?.id ?? "";

  const detailDocumentId = search.document;
  if (detailDocumentId !== undefined) {
    return (
      <DocumentDetail
        documentId={detailDocumentId}
        search={search}
        canMutate={workspace != null && workspace.currentRole !== "viewer"}
        onNavigate={(next) => void navigate({ to: "/documents", search: next, replace: true })}
        onShowConnections={(revision) => void navigate({ to: "/graph", search: { workspace: workspaceId, rootType: "document", rootId: detailDocumentId, rootRevision: revision } })}
      />
    );
  }

  return <DocumentLibrary workspaceId={workspaceId} workspaceName={workspace?.name ?? t("shell.noWorkspace")} canMutate={workspace != null && workspace.currentRole !== "viewer"} search={search} onNavigate={(next) => void navigate({ to: "/documents", search: next, replace: true })} />;
}

function DocumentLibrary({ workspaceId, workspaceName, canMutate, search, onNavigate }: {
  readonly workspaceId: string;
  readonly workspaceName: string;
  readonly canMutate: boolean;
  readonly search: DocumentsSearch;
  readonly onNavigate: (next: DocumentsSearch) => void;
}) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [query, setQuery] = useState("");
  const [submittedQuery, setSubmittedQuery] = useState("");
  const [filter, setFilter] = useState<LibraryFilter>("all");
  const handleUploaded = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: documentsQueryKeys.all });
  }, [queryClient]);
  const uploadQueue = useDocumentUploadQueue(workspaceId, handleUploaded);

  const documentsQuery = useQuery({
    queryKey: submittedQuery.length > 0 ? documentsQueryKeys.search(workspaceId, submittedQuery) : documentsQueryKeys.list(workspaceId),
    enabled: workspaceId.length > 0,
    queryFn: ({ signal }) => submittedQuery.length > 0
      ? documentApi.search(workspaceId, submittedQuery, signal)
      : documentApi.list(workspaceId, signal),
  });
  const documents = documentsQuery.data?.items ?? [];
  const visibleDocuments = useMemo(() => documents.filter((document) => matchesFilter(document.ingestState, filter)), [documents, filter]);
  const readyCount = documents.filter((document) => document.ingestState === "ready").length;

  const submitSearch = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setSubmittedQuery(query.trim());
  };
  const chooseFiles = (files: FileList | readonly File[] | null | undefined): void => {
    if (!canMutate || files === undefined || files === null || workspaceId.length === 0) return;
    uploadQueue.enqueue(Array.from(files));
  };
  const handleDrop = (event: DragEvent<HTMLElement>): void => {
    event.preventDefault();
    chooseFiles(event.dataTransfer.files);
  };

  return (
    <section className={styles.library} aria-labelledby="documents-title">
      <header className={styles.header}>
        <div>
          <p className={styles.eyebrow}>{workspaceName}</p>
          <h1 id="documents-title">{t("documents.title")}</h1>
          <p className={styles.subtitle}>{t("documents.pageDescription")}</p>
        </div>
        <div className={styles.headerActions}>
          <span className={styles.authorityNote}><ShieldCheck size={16} aria-hidden="true" />{t("documents.readyCount", { count: readyCount })}</span>
          <button type="button" className={styles.primaryButton} onClick={() => fileInput.current?.click()} disabled={!canMutate || workspaceId.length === 0}>
            <UploadCloud size={17} aria-hidden="true" />{t("documents.upload")}
          </button>
        </div>
      </header>

      <form className={styles.toolbar} onSubmit={submitSearch}>
        <div className={styles.searchField}>
          <label htmlFor="document-search">{t("documents.search")}</label>
          <div className={styles.searchControl}>
            <input id="document-search" value={query} onChange={(event) => setQuery(event.currentTarget.value)} placeholder={t("documents.searchPlaceholder")} maxLength={500} />
            <button type="submit" className={styles.primaryButton}><Search size={16} aria-hidden="true" />{t("documents.search")}</button>
          </div>
        </div>
        <fieldset className={styles.filterGroup}>
          <legend>{t("documents.filters")}</legend>
          <div className={styles.filterButtons}>
            {FILTERS.map((value) => (
              <button key={value} type="button" className={styles.filterButton} data-selected={filter === value} aria-pressed={filter === value} onClick={() => setFilter(value)}>
                {filterLabel(value, t)}
              </button>
            ))}
          </div>
        </fieldset>
      </form>

      <input
        ref={fileInput}
        className={styles.fileInput}
        type="file"
        multiple
        onChange={(event) => {
          chooseFiles(event.currentTarget.files);
          event.currentTarget.value = "";
        }}
      />
      <section className={styles.dropZone} data-active={uploadQueue.entries.length > 0 ? "true" : "false"} onDragOver={(event) => event.preventDefault()} onDrop={handleDrop} aria-label={t("documents.dropHint")}>
        <UploadCloud size={20} aria-hidden="true" />
        <span>{t("documents.dropHint")}</span>
        <button type="button" className={styles.textButton} onClick={() => fileInput.current?.click()} disabled={!canMutate || workspaceId.length === 0}>{t("documents.chooseFile")}</button>
      </section>
      <UploadQueue entries={uploadQueue.entries} locale={i18n.language} onOpenDocument={(documentId) => onNavigate(withDocument(search, documentId))} onRetry={uploadQueue.retry} onCancel={uploadQueue.cancel} onRemove={uploadQueue.remove} />

      <div className={styles.libraryMeta}>
        <span>{`${visibleDocuments.length} · ${t("documents.title")}`}</span>
        {documentsQuery.isPending ? <span role="status">{t("documents.loading")}</span> : documentsQuery.isFetching ? <span role="status">{t("documents.refreshing")}</span> : null}
        {submittedQuery.length > 0 ? <button type="button" className={styles.textButton} onClick={() => { setQuery(""); setSubmittedQuery(""); }}>{t("documents.clearSearch")}</button> : null}
      </div>
      {documentsQuery.error !== null && !documentsQuery.isPending ? (
        <InlineState
          title={submittedQuery.length > 0 ? t("documents.searchError") : t("documents.listError")}
          description={t("documents.errorDescription")}
          action={<button type="button" onClick={() => void documentsQuery.refetch()}>{t("common.retry")}</button>}
        />
      ) : null}
      {workspaceId.length > 0 && documentsQuery.isPending ? <DocumentSkeletonList /> : null}
      {workspaceId.length > 0 && documentsQuery.error === null && !documentsQuery.isPending && visibleDocuments.length === 0 ? (
        <InlineState title={submittedQuery.length > 0 ? t("documents.noResults") : t("documents.empty")} description={t("documents.emptyDescription")} action={<button type="button" className={styles.primaryButton} onClick={() => fileInput.current?.click()} disabled={!canMutate}>{t("documents.uploadDocument")}</button>} />
      ) : null}
      {workspaceId.length > 0 && documentsQuery.error === null && !documentsQuery.isPending && visibleDocuments.length > 0 ? (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <caption className={styles.visuallyHidden}>{t("documents.title")}</caption>
            <thead><tr><th scope="col">{t("documents.selected")}</th><th scope="col">{t("documents.state")}</th><th scope="col">{t("documents.capability")}</th><th scope="col">{t("documents.version")}</th><th scope="col">{t("documents.size")}</th><th scope="col">{t("documents.modified")}</th><th scope="col"><span className={styles.visuallyHidden}>{t("documents.open")}</span></th></tr></thead>
            <tbody>
              {visibleDocuments.map((document) => (
                <DocumentRow key={document.id} document={document} locale={i18n.language} onOpen={() => onNavigate(withDocument(search, document.id))} />
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
export function UploadQueue({ entries, locale, onOpenDocument, onRetry, onCancel, onRemove }: {
  readonly entries: readonly DocumentUploadQueueEntry[];
  readonly locale: string;
  readonly onOpenDocument?: (documentId: string) => void;
  readonly onRetry: (id: string) => void;
  readonly onCancel: (id: string) => void;
  readonly onRemove: (id: string) => void;
}) {
  const { t } = useTranslation();
  if (entries.length === 0) return null;
  return (
    <section className={styles.uploadQueuePanel} aria-labelledby="document-upload-queue-title">
      <div className={styles.panelHeader}>
        <h2 id="document-upload-queue-title">{t("documents.uploadQueue.title")}</h2>
        <span className={styles.secondaryText}>{t("documents.uploadQueue.count", { count: entries.length })}</span>
      </div>
      <ol className={styles.uploadQueue}>
        {entries.map((entry) => {
          const active = entry.phase === "queued" || entry.phase === "hashing" || entry.phase === "uploading";
          const retryable = entry.phase === "error" || entry.phase === "canceled";
          const percent = entry.progress === null || entry.progress.total <= 0 ? null : Math.min(100, Math.round((entry.progress.loaded / entry.progress.total) * 100));
          return (
            <li key={entry.id} className={styles.uploadQueueItem} data-phase={entry.phase}>
              <div className={styles.uploadQueueMain}>
                <div className={styles.uploadQueueName}>
                  <FileText size={17} aria-hidden="true" />
                  <strong>{entry.file.name}</strong>
                  <span>{formatBytes(String(entry.file.size), locale)}</span>
                </div>
                <div className={styles.uploadQueueStatus}>
                  <span>{t(`documents.uploadQueue.states.${entry.phase}`)}</span>
                  {entry.result !== null ? <StatusLabel state={entry.result.document.ingestState} /> : null}
                </div>
                {active ? (
                  <div className={styles.uploadQueueProgress}>
                    <progress value={percent ?? undefined} max={100} aria-label={t(`documents.uploadQueue.states.${entry.phase}`)} />
                    {percent !== null ? <span>{percent}%</span> : null}
                  </div>
                ) : null}
                {entry.result !== null ? <p className={styles.uploadQueueResult}>{t("documents.uploadQueue.submitted", { name: entry.result.document.displayName })}</p> : null}
                {entry.phase === "error" ? <p className={styles.uploadQueueError} role="alert">{t("documents.uploadQueue.error")}</p> : null}
              </div>
              <div className={styles.uploadQueueActions}>
                {entry.result !== null && onOpenDocument !== undefined ? <button type="button" className={styles.queueAction} onClick={() => onOpenDocument(entry.result?.document.id ?? "")}><ExternalLink size={15} aria-hidden="true" />{t("documents.uploadQueue.open")}</button> : null}
                {active ? <button type="button" className={styles.queueAction} onClick={() => onCancel(entry.id)}><Ban size={15} aria-hidden="true" />{t("documents.uploadQueue.cancel")}</button> : null}
                <button type="button" className={styles.queueIconButton} onClick={() => onRemove(entry.id)} aria-label={`${t("documents.uploadQueue.remove")}: ${entry.file.name}`}><X size={16} aria-hidden="true" /></button>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function DocumentSkeletonList() {
  const { t } = useTranslation();
  return (
    <div className={styles.skeletonList} aria-label={t("documents.loading")} aria-busy="true">
      {[0, 1, 2].map((item) => <div key={item} className={styles.skeletonRow}><span /><span /><span /></div>)}
    </div>
  );
}

function DocumentRow({ document, locale, onOpen }: { readonly document: DocumentProjection; readonly locale: string; readonly onOpen: () => void }) {
  const { t } = useTranslation();
  const canDownload = document.ingestState === "ready" && document.currentVersionId !== null;
  const download = async (): Promise<void> => {
    if (!canDownload) return;
    const result = await documentApi.downloadCurrent(document.id);
    triggerDownload(result.blob, result.filename ?? document.displayName);
  };
  return (
    <tr className={styles.documentRow} data-state={document.ingestState}>
      <th scope="row">
        <button type="button" className={styles.documentName} onClick={onOpen}>
          <FileText size={19} aria-hidden="true" />
          <span><strong>{document.displayName}</strong><small>{fileTypeLabel(document)}</small></span>
          <ChevronRight size={17} aria-hidden="true" />
        </button>
      </th>
      <td data-label={t("documents.state")}><StatusLabel state={document.ingestState} /></td>
      <td data-label={t("documents.capability")}><span className={styles.capability}>{capabilityLabel(document.aiCapability, t)}</span></td>
      <td data-label={t("documents.version")}>{document.currentVersionId === null ? "—" : `#${document.revision}`}</td>
      <td data-label={t("documents.size")}>{document.currentByteLength === null ? "—" : formatBytes(document.currentByteLength, locale)}</td>
      <td data-label={t("documents.modified")}><time dateTime={document.updatedAt}>{formatDate(document.updatedAt, locale)}</time></td>
      <td className={styles.rowActions}><button type="button" className={styles.iconButton} onClick={() => void download()} disabled={!canDownload} aria-label={`${t("documents.download")}: ${document.displayName}`}><Download size={17} aria-hidden="true" /></button></td>
    </tr>
  );
}

function StatusLabel({ state }: { readonly state: UploadState }) {
  const { t } = useTranslation();
  const Icon = state === "ready" ? ShieldCheck : state === "scanning" || state === "quarantined" || state === "uploading" ? ScanLine : ShieldAlert;
  return <span className={styles.status} data-state={state}><Icon size={16} aria-hidden="true" /><span>{t(`documents.ingestStates.${state}`)}</span></span>;
}

function InlineState({ title, description, action }: { readonly title: string; readonly description: string; readonly action: ReactNode }) {
  return <section className={styles.inlineState} aria-live="polite"><h2>{title}</h2><p>{description}</p>{action}</section>;
}

function DocumentDetail({ documentId, search, canMutate, onNavigate, onShowConnections }: { readonly documentId: string; readonly search: DocumentsSearch; readonly canMutate: boolean; readonly onNavigate: (next: DocumentsSearch) => void; readonly onShowConnections: (revision: string) => void }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [editorText, setEditorText] = useState("");
  const [dirty, setDirty] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [notice, setNotice] = useState("");
  const [editorGate, setEditorGate] = useState<DocumentEditorGate | null>(null);
  const [compareText, setCompareText] = useState<string | null>(null);
  const officeEditButtonRef = useRef<HTMLButtonElement>(null);
  const documentQuery = useQuery({ queryKey: documentsQueryKeys.detail(documentId), queryFn: ({ signal }) => documentApi.get(documentId, signal) });
  const document = documentQuery.data;
  const markdown = document !== undefined && document.surfaceCapabilities.editor === "markdown";
  const office = document !== undefined && document.surfaceCapabilities.editor === "office";
  const versionsQuery = useQuery({ queryKey: documentsQueryKeys.versions(documentId), enabled: document !== undefined, queryFn: ({ signal }) => documentApi.versions(documentId, signal) });
  const draftQuery = useQuery({ queryKey: documentsQueryKeys.draft(documentId), enabled: markdown, queryFn: ({ signal }) => documentApi.getDraft(documentId, signal) });
  const gitQuery = useQuery({ queryKey: documentsQueryKeys.git(documentId), enabled: document !== undefined, retry: false, queryFn: ({ signal }) => documentApi.gitStatus(documentId, signal) });
  const outputsQuery = useQuery({ queryKey: documentsQueryKeys.outputs(documentId), enabled: document !== undefined, retry: false, queryFn: ({ signal }) => documentApi.editOutputs(documentId, signal) });
  const redactionsQuery = useQuery({ queryKey: documentsQueryKeys.redactions(documentId), enabled: document !== undefined, retry: false, queryFn: ({ signal }) => documentApi.redactionDerivatives(documentId, signal) });
  const draft = draftQuery.data;
  const dirtyRef = useRef(false);
  useEffect(() => {
    if (draft === undefined || dirtyRef.current) return;
    setEditorText(decodeBase64Utf8(draft.bytesBase64));
  }, [draft]);
  const saveDraft = useMutation({
    retry: false,
    mutationFn: () => {
      if (draft === undefined) throw new Error("Markdown draft authority is unavailable");
      return documentApi.saveDraft(documentId, { baseVersionId: draft.baseVersionId, savedStateVector: draft.savedStateVector, bytesBase64: encodeText(editorText) });
    },
    onSuccess: async () => {
      dirtyRef.current = false;
      setDirty(false);
      setNotice(t("documents.draftSaved"));
      await queryClient.invalidateQueries({ queryKey: documentsQueryKeys.draft(documentId) });
    },
    onError: () => setNotice(t("documents.draftSaveFailed")),
  });
  const saveVersion = useMutation({
    retry: false,
    mutationFn: () => {
      if (document === undefined || draft === undefined) throw new Error("Markdown save authority is unavailable");
      return documentApi.saveMarkdown(createMarkdownSaveCommand(document, draft, editorText));
    },
    onSuccess: async (saved) => {
      dirtyRef.current = false;
      setDirty(false);
      setConflict(false);
      setNotice(t("documents.savedVersion", { version: saved.revision }));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: documentsQueryKeys.detail(documentId) }),
        queryClient.invalidateQueries({ queryKey: documentsQueryKeys.versions(documentId) }),
        queryClient.invalidateQueries({ queryKey: documentsQueryKeys.draft(documentId) }),
      ]);
    },
    onError: (error) => {
      if (error instanceof ApiError && error.code === "SAVE_CONFLICT") {
        setConflict(true);
        return;
      }
      setNotice(t("documents.versionSaveFailed"));
    },
  });
  const outputMutation = useMutation({
    retry: false,
    mutationFn: (input: { readonly id: string; readonly action: "stage" | "discard" }) => input.action === "stage"
      ? documentApi.stageEditOutput(documentId, input.id)
      : documentApi.discardEditOutput(documentId, input.id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: documentsQueryKeys.outputs(documentId) }),
    onError: () => setNotice(t("documents.outputActionFailed")),
  });
  const editorOpen = useMutation({
    retry: false,
    mutationFn: () => documentApi.openEditor(documentId, "edit", AbortSignal.timeout(OFFICE_EDITOR_OPEN_TIMEOUT_MS)),
    onSuccess: (gate) => {
      setEditorGate(gate);
      setNotice(t(`documents.editorStates.${gate.status}`));
    },
    onError: () => setNotice(t("documents.officeEditorError")),
  });
  const editorFinalize = useMutation({
    retry: false,
    mutationFn: () => documentApi.finalizeEditor(documentId, AbortSignal.timeout(OFFICE_EDITOR_FINALIZE_TIMEOUT_MS)),
    onSuccess: async (result) => {
      setNotice(t(`documents.editorStates.${result.status}`));
      if (result.status === "closed") {
        setEditorGate(null);
        window.requestAnimationFrame(() => officeEditButtonRef.current?.focus());
      } else {
        setEditorGate((current) => current === null ? null : {
          ...current,
          document: result.document,
          status: result.status,
          bootstrapToken: null,
          expiresAt: null,
        });
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: documentsQueryKeys.detail(documentId) }),
        queryClient.invalidateQueries({ queryKey: documentsQueryKeys.versions(documentId) }),
        queryClient.invalidateQueries({ queryKey: documentsQueryKeys.outputs(documentId) }),
      ]);
    },
    onError: () => setNotice(t("documents.officeFinalizeError")),
  });
  const editorReset = useMutation({
    retry: false,
    mutationFn: () => documentApi.closeEditor(documentId, AbortSignal.timeout(OFFICE_EDITOR_RESET_TIMEOUT_MS)),
    onSuccess: async () => {
      setEditorGate(null);
      setNotice(t("documents.editorStates.closed"));
      window.requestAnimationFrame(() => officeEditButtonRef.current?.focus());
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: documentsQueryKeys.detail(documentId) }),
        queryClient.invalidateQueries({ queryKey: documentsQueryKeys.versions(documentId) }),
        queryClient.invalidateQueries({ queryKey: documentsQueryKeys.outputs(documentId) }),
      ]);
    },
    onError: () => setNotice(t("documents.officeResetError")),
  });
  const downloadVersion = async (versionId: string): Promise<void> => {
    try {
      const result = await documentApi.downloadVersion(documentId, versionId);
      triggerDownload(result.blob, result.filename ?? `${document?.displayName ?? "document"}-${versionId}`);
    } catch {
      setNotice(t("documents.versionDownloadFailed"));
    }
  };
  const downloadRedaction = async (derivativeId: string): Promise<void> => {
    try {
      const result = await documentApi.downloadRedactionDerivative(documentId, derivativeId);
      triggerDownload(result.blob, result.filename ?? `${document?.displayName ?? "document"}.redacted`);
    } catch {
      setNotice(t("documents.redactionDownloadFailed"));
    }
  };
  const compareCurrent = async (): Promise<void> => {
    try {
      const result = await documentApi.downloadCurrent(documentId);
      setCompareText(await result.blob.text());
    } catch {
      setNotice(t("documents.saveFailed"));
    }
  };
  const reloadConflict = async (): Promise<void> => {
    const preservedText = editorText;
    dirtyRef.current = true;
    setConflict(false);
    setCompareText(null);
    await Promise.all([
      queryClient.refetchQueries({ queryKey: documentsQueryKeys.detail(documentId) }),
      queryClient.refetchQueries({ queryKey: documentsQueryKeys.draft(documentId) }),
    ]);
    setEditorText(preservedText);
    setDirty(true);
  };
  const download = async (): Promise<void> => {
    if (document === undefined || document.ingestState !== "ready") return;
    try {
      const result = await documentApi.downloadCurrent(document.id);
      triggerDownload(result.blob, result.filename ?? document.displayName);
    } catch {
      setNotice(t("documents.downloadError"));
    }
  };
  if (documentQuery.isPending) return <section className={styles.detailLoading} aria-busy="true"><LoaderCircle className={styles.spinner} size={20} aria-hidden="true" /><span>{t("documents.loading")}</span></section>;
  if (documentQuery.error !== null || document === undefined) return <InlineState title={t("documents.detailError")} description={t("documents.detailErrorDescription")} action={<div className={styles.fenceActions}><button type="button" onClick={() => onNavigate(withDocument(search, null))}>{t("documents.back")}</button><button type="button" className={styles.primaryButton} onClick={() => void documentQuery.refetch()}>{t("common.retry")}</button></div>} />;
  const canEdit = canMutate && document.ingestState === "ready" && document.currentVersionId !== null && document.surfaceCapabilities.editor !== null;
  const officeActive = editorGate !== null && editorGate.status !== "closed";
  const officeNavigationLocked = editorGate !== null && (editorGate.status === "opening" || editorGate.status === "active" || editorGate.status === "saving" || editorGate.status === "finalizing");
  const editorReady = canEdit && markdown && draftQuery.isSuccess;
  return (
    <section className={styles.detail} aria-labelledby="document-detail-title">
      <header className={styles.detailHeader}>
        <button type="button" className={styles.backButton} onClick={() => onNavigate(withDocument(search, null))} disabled={officeNavigationLocked}><ArrowLeft size={17} aria-hidden="true" />{t("documents.back")}</button>
        <div className={styles.detailHeading}><p className={styles.eyebrow}>{fileTypeLabel(document)}</p><h1 id="document-detail-title">{document.displayName}</h1><div className={styles.detailBadges}><StatusLabel state={document.ingestState} /><span>{capabilityLabel(document.aiCapability, t)}</span><span>{t("documents.version")} {document.revision}</span></div></div>
        <div className={styles.detailActions}>
          <button type="button" onClick={() => void download()} disabled={document.ingestState !== "ready"}><Download size={16} aria-hidden="true" />{t("documents.download")}</button>
          <button type="button" onClick={() => onShowConnections(document.revision)}><GitBranch size={16} aria-hidden="true" />{t("documents.showConnections")}</button>
          {office && !officeActive ? <button ref={officeEditButtonRef} type="button" className={styles.primaryButton} onClick={() => editorOpen.mutate()} disabled={!canEdit || editorOpen.isPending}>{t("documents.edit")}</button> : null}
        </div>
      </header>
      <div className={styles.detailMeta}><span>{t("documents.modified")} {formatDate(document.updatedAt, i18n.language)}</span><span>{document.currentByteLength === null ? "—" : formatBytes(document.currentByteLength, i18n.language)}</span></div>
      {document.ingestState !== "ready" ? <AuthorityFence state={document.ingestState} /> : null}
      {conflict ? <section className={styles.conflictFence} aria-labelledby="document-conflict-title"><ShieldAlert size={21} aria-hidden="true" /><div><h2 id="document-conflict-title">{t("documents.conflictTitle")}</h2><p>{t("documents.conflictDescription")}</p><div className={styles.fenceActions}><button type="button" onClick={() => void compareCurrent()}>{t("documents.compare")}</button><button type="button" className={styles.primaryButton} onClick={() => void reloadConflict()}>{t("documents.reloadVersion")}</button></div></div></section> : null}
      {compareText !== null ? <ConflictCompare current={compareText} draft={editorText} onClose={() => setCompareText(null)} /> : null}
      {notice.length > 0 ? <p className={styles.notice} role="status">{notice}</p> : null}
      <div className={styles.detailGrid}>
        <main className={styles.editorPanel}>
          {markdown ? <MarkdownEditor text={editorText} onText={(value) => { dirtyRef.current = true; setDirty(true); setEditorText(value); }} onSaveDraft={() => saveDraft.mutate()} onSaveVersion={() => saveVersion.mutate()} pending={saveDraft.isPending || saveVersion.isPending} dirty={dirty} editable={editorReady} draftLoading={draftQuery.isPending} draftError={draftQuery.error !== null} /> : editorGate !== null && (editorGate.status === "opening" || editorGate.status === "active") && editorGate.bootstrapToken !== null ? <OfficeEditor gate={editorGate} closing={editorFinalize.isPending} closeFailed={editorFinalize.isError} retrying={editorOpen.isPending} onRetry={() => editorOpen.mutate()} onClose={() => editorFinalize.mutate()} /> : office ? <OfficeAuthority document={document} gate={editorGate} opening={editorOpen.isPending} finalizing={editorFinalize.isPending} resetting={editorReset.isPending} resetFailed={editorReset.isError} onFinalize={() => editorFinalize.mutate()} onReset={() => editorReset.mutate()} /> : <DocumentViewer document={document} />}
        </main>
        <aside className={styles.inspector} aria-label={t("documents.metadata")}>
          <DocumentInspector document={document} locale={i18n.language} canMutate={canMutate} versions={versionsQuery.data?.items ?? []} git={gitQuery.data} outputs={outputsQuery.data?.items ?? []} redactions={redactionsQuery.data ?? []} outputPending={outputMutation.isPending} onOutput={(id, action) => outputMutation.mutate({ id, action })} onDownloadVersion={(versionId) => void downloadVersion(versionId)} onDownloadRedaction={(derivativeId) => void downloadRedaction(derivativeId)} />
        </aside>
      </div>
    </section>
  );
}
function DocumentViewer({ document }: { readonly document: DocumentProjection }) {
  const { t } = useTranslation();
  const preview = document.surfaceCapabilities.preview;
  const [attempt, setAttempt] = useState(0);
  const [text, setText] = useState<string | null>(null);
  const [source, setSource] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let disposed = false;
    let objectUrl: string | null = null;
    setText(null);
    setSource(null);
    setFailed(false);
    if (document.ingestState !== "ready" || preview === "download") return;
    const controller = new AbortController();
    void documentApi.downloadCurrent(document.id, controller.signal).then(async (result) => {
      if (disposed) return;
      if (preview === "image" || preview === "pdf") {
        objectUrl = URL.createObjectURL(result.blob);
        setSource(objectUrl);
        return;
      }
      setText(await result.blob.text());
    }).catch(() => {
      if (!disposed && !controller.signal.aborted) setFailed(true);
    });
    return () => {
      disposed = true;
      controller.abort();
      if (objectUrl !== null) URL.revokeObjectURL(objectUrl);
    };
  }, [attempt, document.id, document.ingestState, preview]);

  if (preview === "download") {
    return <section className={styles.viewerPanel}><h2>{t("documents.downloadOnlyTitle")}</h2><p>{t("documents.downloadOnlyDescription")}</p></section>;
  }
  if (document.ingestState !== "ready") return <section className={styles.viewerPanel}><h2>{t("documents.preview")}</h2><p>{t("documents.viewerUnavailable")}</p></section>;
  if (failed) return <section className={styles.viewerPanel}><h2>{t("documents.viewerError")}</h2><p>{t("documents.viewerErrorDescription")}</p><button type="button" className={styles.primaryButton} onClick={() => setAttempt((value) => value + 1)}>{t("common.retry")}</button></section>;
  if (preview === "image" && source !== null) return <figure className={styles.imageViewer}><img src={source} alt={document.displayName} /><figcaption>{t("documents.preview")}</figcaption></figure>;
  if (preview === "pdf" && source !== null) return <iframe className={styles.pdfViewer} src={source} title={document.displayName} />;
  if (text === null) return <section className={styles.detailLoading} aria-busy="true"><LoaderCircle className={styles.spinner} size={20} aria-hidden="true" /><span>{t("documents.loading")}</span></section>;
  return <section className={styles.viewerPanel}><div className={styles.panelHeader}><h2>{t("documents.preview")}</h2><span className={styles.dirtyState}>{t("documents.readOnlyAi")}</span></div><pre className={styles.textViewer}>{text}</pre></section>;
}

function MarkdownEditor({ text, onText, onSaveDraft, onSaveVersion, pending, dirty, editable, draftLoading, draftError }: { readonly text: string; readonly onText: (value: string) => void; readonly onSaveDraft: () => void; readonly onSaveVersion: () => void; readonly pending: boolean; readonly dirty: boolean; readonly editable: boolean; readonly draftLoading: boolean; readonly draftError: boolean }) {
  const { t } = useTranslation();
  return <section className={styles.markdownEditor} aria-labelledby="markdown-editor-title"><div className={styles.panelHeader}><div><p className={styles.eyebrow}>{t("documents.source")}</p><h2 id="markdown-editor-title">{t("documents.editing")}</h2></div><span className={styles.dirtyState}>{draftError ? t("documents.saveFailed") : draftLoading ? t("documents.loading") : !editable ? t("documents.blocked") : dirty ? t("documents.saving") : t("documents.draftSaved")}</span></div>{draftError ? <p className={styles.warningText}>{t("documents.saveFailed")}</p> : null}<div className={styles.editorColumns}><label className={styles.editorField}><span>{t("documents.source")}</span><textarea value={text} onChange={(event) => onText(event.currentTarget.value)} spellCheck={false} readOnly={!editable} disabled={!editable && draftError} /></label><div className={styles.preview} aria-label={t("documents.preview")}><p className={styles.previewLabel}>{t("documents.preview")}</p><MarkdownPreview text={text} /></div></div><div className={styles.editorActions}><button type="button" onClick={onSaveDraft} disabled={pending || !dirty || !editable}>{t("documents.saveDraft")}</button><button type="button" className={styles.primaryButton} onClick={onSaveVersion} disabled={pending || !dirty || !editable}>{t("documents.saveVersion")}</button></div></section>;
}

function MarkdownPreview({ text }: { readonly text: string }) {
  return (
    <div className={styles.markdownPreview}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        skipHtml
        urlTransform={markdownUrlTransform}
        components={{
          a: ({ children, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer">{children}</a>,
          img: ({ alt }) => alt === undefined ? null : <span>{alt}</span>,
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}

function markdownUrlTransform(url: string): string {
  const normalized = url.trim();
  if (normalized.length === 0) return "";
  try {
    const parsed = new URL(normalized, window.location.origin);
    return parsed.protocol === "http:" || parsed.protocol === "https:" || parsed.protocol === "mailto:" ? normalized : "";
  } catch {
    return "";
  }
}
function ConflictCompare({ current, draft, onClose }: { readonly current: string; readonly draft: string; readonly onClose: () => void }) {
  const { t } = useTranslation();
  return <section className={styles.comparePanel} aria-labelledby="document-compare-title"><header className={styles.panelHeader}><div><p className={styles.eyebrow}>{t("documents.compare")}</p><h2 id="document-compare-title">{t("documents.compareTitle")}</h2></div><button type="button" className={styles.textButton} onClick={onClose}>{t("documents.close")}</button></header><p className={styles.secondaryText}>{t("documents.compareDescription")}</p><div className={styles.compareColumns}><article className={styles.comparePane}><h3>{t("documents.currentVersion")}</h3><pre>{current}</pre></article><article className={styles.comparePane}><h3>{t("documents.draftSaved")}</h3><pre>{draft}</pre></article></div></section>;
}

function OfficeAuthority({ document, gate, opening, finalizing, resetting, resetFailed, onFinalize, onReset }: { readonly document: DocumentProjection; readonly gate: DocumentEditorGate | null; readonly opening: boolean; readonly finalizing: boolean; readonly resetting: boolean; readonly resetFailed: boolean; readonly onFinalize: () => void; readonly onReset: () => void }) {
  const { t } = useTranslation();
  const status = gate?.status;
  if (gate === null && !opening) {
    return document.ingestState === "ready" ? <p className={styles.secondaryText}>{t("documents.officeEditorAvailable")}</p> : null;
  }
  const terminal = status === "save_failed" || status === "authorization_revoked" || status === "finalizing";
  const title = status === "authorization_revoked"
    ? t("documents.officeRevoked")
    : status === "save_failed"
      ? t("documents.officeSaveFailed")
      : status === "finalizing"
        ? t("documents.officeFinalizeError")
        : t("documents.officeLifecycle");
  const recoveringTerminal = status === "save_failed" || status === "authorization_revoked";
  const description = document.ingestState !== "ready"
    ? t("documents.blocked")
    : opening
      ? t("documents.officeOpening")
      : status === "finalizing"
        ? t(finalizing ? "documents.officeFinalizing" : "documents.officeFinalizeStalled")
        : recoveringTerminal && resetting
          ? t("documents.officeResetting")
          : recoveringTerminal && resetFailed
            ? t("documents.officeResetError")
            : status === undefined
              ? t("documents.officeEditorAvailable")
              : t(`documents.editorStates.${status}`);
  return <section className={terminal ? styles.authorityFence : styles.officePanel} aria-live="polite">{terminal ? <ShieldAlert size={24} aria-hidden="true" /> : <GitBranch size={24} aria-hidden="true" />}<div><h2>{title}</h2><p>{description}</p>{status === "finalizing" ? <button type="button" className={styles.textButton} onClick={onFinalize} disabled={finalizing}>{finalizing ? t("documents.officeFinalizing") : t("common.retry")}</button> : recoveringTerminal ? <button type="button" className={styles.textButton} onClick={onReset} disabled={resetting}>{resetting ? t("documents.officeResetting") : resetFailed ? t("common.retry") : t("documents.officeResetAction")}</button> : null}</div></section>;
}

function DocumentInspector({ document, locale, canMutate, versions, git, outputs, redactions, outputPending, onOutput, onDownloadVersion, onDownloadRedaction }: { readonly document: DocumentProjection; readonly locale: string; readonly canMutate: boolean; readonly versions: readonly DocumentVersionProjection[]; readonly git: DocumentGitStatus | undefined; readonly outputs: readonly DocumentEditOutput[]; readonly redactions: readonly DocumentRedactionDerivative[]; readonly outputPending: boolean; readonly onOutput: (id: string, action: "stage" | "discard") => void; readonly onDownloadVersion: (versionId: string) => void; readonly onDownloadRedaction: (derivativeId: string) => void }) {
  const { t } = useTranslation();
  return (
    <div className={styles.inspectorStack}>
      <section className={styles.inspectorSection}>
        <h2>{t("documents.metadata")}</h2>
        <dl className={styles.metadataList}>
          <div><dt>{t("documents.fileType")}</dt><dd>{fileTypeLabel(document)}</dd></div>
          <div><dt>{t("documents.state")}</dt><dd><StatusLabel state={document.ingestState} /></dd></div>
          <div><dt>{t("documents.capability")}</dt><dd>{capabilityLabel(document.aiCapability, t)}</dd></div>
          <div><dt>{t("documents.size")}</dt><dd>{document.currentByteLength === null ? "—" : formatBytes(document.currentByteLength, locale)}</dd></div>
        </dl>
      </section>
      <details className={styles.inspectorSection}>
        <summary>{t("documents.history")}<span>{versions.length}</span></summary>
        {versions.length === 0 ? <p className={styles.secondaryText}>—</p> : <ol className={styles.versionList}>{versions.map((version) => <li key={version.id}><span><strong>{t("documents.version")} {version.sequence}</strong><small>{t(`documents.sourceTypes.${version.source}`)} · {formatDate(version.createdAt, locale)}</small></span><button type="button" className={styles.iconButton} onClick={() => onDownloadVersion(version.id)} aria-label={`${t("documents.download")} ${version.sequence}`}><Download size={16} aria-hidden="true" /></button></li>)}</ol>}
      </details>
      <details className={styles.inspectorSection}>
        <summary>{t("documents.gitStatus")}</summary>
        <p className={styles.gitState}><GitBranch size={16} aria-hidden="true" />{git === undefined ? t("documents.blocked") : gitStateLabel(git.state, t)}</p>
        {git?.state === "conflicted" ? <p className={styles.warningText}>{t("documents.gitConflict")}</p> : null}
      </details>
      <details className={styles.inspectorSection}>
        <summary>{t("documents.proposal")}<span>{outputs.length}</span></summary>
        {outputs.length === 0 ? <p className={styles.secondaryText}>{t("documents.noOutputs")}</p> : outputs.map((output) => {
          const stageable = output.state === "quarantined" || output.state === "scanning";
          const discardable = output.state !== "promoted" && output.state !== "discarded";
          return <article key={output.id} className={styles.output}><strong>{t("documents.proposalText")}</strong><span>{outputStateLabel(output.state, t)}</span>{stageable || discardable ? <div className={styles.outputActions}>{discardable ? <button type="button" onClick={() => onOutput(output.id, "discard")} disabled={!canMutate || outputPending}>{t("documents.discardOutput")}</button> : null}{stageable ? <button type="button" className={styles.primaryButton} onClick={() => onOutput(output.id, "stage")} disabled={!canMutate || outputPending}>{t("documents.stageOutput")}</button> : null}</div> : null}</article>;
        })}
      </details>
      <RedactionSection redactions={redactions} onDownload={onDownloadRedaction} />
    </div>
  );
}

function RedactionSection({ redactions, onDownload }: { readonly redactions: readonly DocumentRedactionDerivative[]; readonly onDownload: (derivativeId: string) => void }) {
  const { t } = useTranslation();
  return (
    <details className={styles.inspectorSection}>
      <summary>{t("documents.redactions")}<span>{redactions.length}</span></summary>
      {redactions.length === 0 ? <p className={styles.secondaryText}>{t("documents.noRedactions")}</p> : <ol className={styles.redactionList}>{redactions.map((redaction) => <li key={redaction.id}><div><strong>{t(`documents.redactionStates.${redaction.state}`)}</strong><small>{t("documents.redactionRegions", { count: redaction.regions.length })}</small></div><button type="button" className={styles.iconButton} onClick={() => onDownload(redaction.id)} disabled={redaction.state !== "verified"} aria-label={`${t("documents.download")} ${t("documents.redactions")}`}><Download size={16} aria-hidden="true" /></button></li>)}</ol>}
    </details>
  );
}

function AuthorityFence({ state }: { readonly state: UploadState }) {
  const { t } = useTranslation();
  const title = state === "rejected_unsafe" ? t("documents.unsafe") : state === "failed_transient" ? t("documents.scannerUnavailable") : t("documents.scanning");
  return <section className={styles.authorityFence} aria-live="polite"><ShieldAlert size={21} aria-hidden="true" /><div><h2>{title}</h2><p>{t(`documents.ingestStates.${state}`)} · {t("documents.blocked")}</p></div></section>;
}

function matchesFilter(state: UploadState, filter: LibraryFilter): boolean {
  if (filter === "all") return true;
  if (filter === "ready") return state === "ready";
  if (filter === "scanning") return state === "uploading" || state === "quarantined" || state === "scanning";
  return state === "rejected_unsafe" || state === "failed_transient";
}

function filterLabel(filter: LibraryFilter, t: TFunction): string {
  switch (filter) {
    case "all": return t("documents.all");
    case "ready": return t("documents.readyFilter");
    case "scanning": return t("documents.scanningFilter");
    case "attention": return t("documents.attentionFilter");
  }
}

function capabilityLabel(capability: DocumentProjection["aiCapability"], t: TFunction): string {
  if (capability === "read_write") return t("documents.readWrite");
  if (capability === "read_only") return t("documents.readOnlyAi");
  return t("documents.blocked");
}

function gitStateLabel(state: DocumentGitStatus["state"], t: TFunction): string {
  return state === "conflicted" ? t("documents.gitConflict") : state;
}
function outputStateLabel(state: DocumentEditOutput["state"], t: TFunction): string {
  if (state === "quarantined" || state === "scanning") return t(`documents.ingestStates.${state}`);
  if (state === "ready") return t("documents.readyFilter");
  if (state === "promoted") return t("documents.versionCurrent");
  if (state === "discarded") return t("documents.close");
  if (state === "rejected_unsafe") return t("documents.unsafe");
  if (state === "failed_transient") return t("documents.scannerUnavailable");
  return t("documents.saveConflict");
}


function fileTypeLabel(document: Pick<DocumentProjection, "displayName" | "mediaType">): string {
  const extension = document.displayName.split(".").pop()?.toUpperCase();
  return extension !== undefined && extension !== document.displayName.toUpperCase() ? extension : document.mediaType.split("/").pop()?.toUpperCase() ?? "FILE";
}

function formatBytes(value: string, locale: string = "ru"): string {
  const bytes = BigInt(value);
  const russian = locale.startsWith("ru");
  const numberLocale = russian ? "ru-RU" : "en-US";
  if (bytes >= 1_048_576n) return `${formatNumber(Number(bytes) / 1_048_576, numberLocale)} ${russian ? "МБ" : "MB"}`;
  if (bytes >= 1_024n) return `${formatNumber(Number(bytes) / 1_024, numberLocale)} ${russian ? "КБ" : "KB"}`;
  return `${bytes.toString()} ${russian ? "Б" : "B"}`;
}

function formatNumber(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value);
}

function formatDate(value: string, locale: string): string {
  return new Intl.DateTimeFormat(locale.startsWith("ru") ? "ru-RU" : "en-US", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }).format(new Date(value));
}


function encodeText(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
